import { Submission } from 'types/evaluation';
import { getPillarDisplayName } from 'constants/awardCategoryPillar';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export type SortDirection = 'asc' | 'desc';

export interface RoleFilter {
  userIsSuperAdmin: boolean;
  userIsPillarPIC: boolean;
  userIsCategoryJudge: boolean;
  userPillar?: string;
  userCategory?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Filter helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns submissions whose text fields match the search query (case-insensitive).
 * Matches against: full_name, project_name, category, pillar, organization_unit.
 */
export function filterBySearchQuery(
  submissions: Submission[],
  query: string
): Submission[] {
  const normalizedQuery = query.toLowerCase();
  return submissions.filter(
    s =>
      s.full_name?.toLowerCase().includes(normalizedQuery) ||
      s.project_name?.toLowerCase().includes(normalizedQuery) ||
      s.category?.toLowerCase().includes(normalizedQuery) ||
      s.pillar?.toLowerCase().includes(normalizedQuery) ||
      s.organization_unit?.toLowerCase().includes(normalizedQuery)
  );
}

/**
 * Returns submissions belonging to the given category.
 */
export function filterByCategory(
  submissions: Submission[],
  category: string
): Submission[] {
  return submissions.filter(s => s.category === category);
}

/**
 * Applies role-based access control to the submission list.
 *
 * - Super Admin → sees everything (no filter applied).
 * - Pillar PIC   → only sees submissions from their pillar.
 * - Category Judge → only sees submissions from their category.
 */
export function filterByRole(
  submissions: Submission[],
  role: RoleFilter
): Submission[] {
  const {
    userIsSuperAdmin,
    userIsPillarPIC,
    userIsCategoryJudge,
    userPillar,
    userCategory,
  } = role;

  if (userIsSuperAdmin) {
    return submissions; // full access — no filtering
  }

  if (userIsPillarPIC && userPillar) {
    const displayUserPillar = getPillarDisplayName(userPillar);
    return submissions.filter(
      s => s.pillar === userPillar || s.pillar === displayUserPillar
    );
  }

  if (userIsCategoryJudge && userCategory) {
    return submissions.filter(s => s.category === userCategory);
  }

  return submissions;
}

/**
 * Filters submissions for the evaluation page based on user role.
 *
 * - AWARD_ADMIN → all submissions (unfiltered)
 * - JUDGE_CATEGORY → all submissions except those from the judge's own pillar (COI)
 */
export function filterSubmissionsForEvaluation(
  submissions: Submission[],
  userRoleType: string,
  userPillarDisplayName: string | null,
  assignedSubmissionIds?: Set<string>
): Submission[] {
  if (userRoleType === 'AWARD_ADMIN') {
    return submissions;
  }

  let filtered = submissions;

  // COI filter: exclude judge's own pillar
  if (userRoleType === 'JUDGE_CATEGORY' && userPillarDisplayName) {
    filtered = filtered.filter(
      submission => submission.pillar !== userPillarDisplayName
    );
  }

  // Assignment filter: only show explicitly assigned submissions.
  // An empty Set means "checked but has no assignments" → show nothing.
  if (userRoleType === 'JUDGE_CATEGORY' && assignedSubmissionIds != null) {
    filtered = filtered.filter(submission =>
      assignedSubmissionIds.has(submission.documentId)
    );
  }

  return filtered;
}

// ─────────────────────────────────────────────────────────────────────────────
// Average-rank calculation
// ─────────────────────────────────────────────────────────────────────────────

const NO_RANK_SENTINEL = 999;

// Cara Menghitung Average Rank
// Gambaran besarnya: Setiap submission dinilai oleh beberapa juri. Fungsi ini mencari tahu "rata-rata, submission ini berada di peringkat berapa menurut semua juri?"

// Langkah-langkahnya:
// 1. Ambil semua juri yang menilai submission ini
// Misal: submission A dinilai oleh 3 juri (Budi, Siti, Andi).

// 2. Untuk setiap juri, hitung peringkat submission ini

// Untuk setiap juri (misal Budi):

// Kumpulkan semua submission lain dalam kategori yang sama yang juga dinilai oleh Budi
// Urutkan dari skor tertinggi ke terendah
// Tentukan peringkat submission A di antara mereka
// Sistem peringkat yang dipakai adalah standard competition ranking — kalau ada nilai yang sama (seri), mereka dapat peringkat yang sama, dan peringkat berikutnya di-skip. Contoh: skor [10, 10, 8] → peringkat [1, 1, 3] (bukan 1, 2, 3).

// 3. Rata-ratakan semua peringkat dari tiap juri

// Budi kasih rank 2, Siti kasih rank 1, Andi kasih rank 2
// → Average rank = (2 + 1 + 2) / 3 = 1.67
// 4. Kalau tidak ada juri yang menilai → hasilnya 999 (sentinel, artinya "tidak punya rank", dan selalu ditaruh paling bawah saat sorting)

// Intinya: Setiap juri meranking submission dalam kategorinya masing-masing, lalu semua ranking itu dirata-rata. Makin kecil angkanya, makin bagus posisinya.

/**
 * Computes the average rank for a submission across all judges who evaluated it,
 * relative to other submissions in the same category.
 *
 * Ranking uses standard competition ranking (ties share the same rank and skip
 * subsequent positions).
 *
 * Returns `NO_RANK_SENTINEL` (999) when there are no judgments or valid ranks.
 */

export function calculateAverageRank(
  submission: Submission,
  allSubmissions: Submission[]
): number {
  const judgments = submission.all_judgments ?? [];

  if (judgments.length === 0) {
    return NO_RANK_SENTINEL;
  }

  const rankPerJudge = judgments.map(judgment => {
    const { judge_email } = judgment;

    // Collect all scores from this judge within the same category
    const sameCategoryScores = allSubmissions
      .filter(s => s.category === submission.category)
      .flatMap(s =>
        (s.all_judgments ?? [])
          .filter(j => j.judge_email === judge_email)
          .map(j => ({
            submissionId: s.id,
            score: j.calculated_weighted_score || j.weighted_total_score || 0,
            isTarget: s.id === submission.id,
          }))
      )
      .sort((a, b) => b.score - a.score); // highest score first

    return computeStandardRank(sameCategoryScores);
  });

  const validRanks = rankPerJudge.filter(r => r > 0);

  if (validRanks.length === 0) {
    return NO_RANK_SENTINEL;
  }

  const sum = validRanks.reduce((acc, r) => acc + r, 0);
  return sum / validRanks.length;
}

// ─────────────────────────────────────────────────────────────────────────────
// Internal ranking helper
// ─────────────────────────────────────────────────────────────────────────────

interface ScoredEntry {
  submissionId: string;
  score: number;
  isTarget: boolean;
}

/**
 * Computes the standard competition rank of the target entry inside an
 * already-sorted (descending) array.
 *
 * Example: scores [10, 10, 8] → ranks [1, 1, 3]
 *
 * Returns 0 when the target entry is not found.
 */
function computeStandardRank(sortedEntries: ScoredEntry[]): number {
  let currentRank = 1;

  for (let i = 0; i < sortedEntries.length; i++) {
    // When the score drops we jump rank to the current position index (1-based)
    if (i > 0 && sortedEntries[i].score !== sortedEntries[i - 1].score) {
      currentRank = i + 1;
    }

    if (sortedEntries[i].isTarget) {
      return currentRank;
    }
  }

  return 0; // target not found (shouldn't happen in normal usage)
}

// ─────────────────────────────────────────────────────────────────────────────
// Per-judge ranking breakdown
// ─────────────────────────────────────────────────────────────────────────────

export interface JudgeRankingEntry {
  rank: number | '-';
  totalEvaluated: number;
}

// Cara Kerja calculateJudgeRankings
// Tujuannya: Berbeda dengan calculateAverageRank yang menghasilkan satu angka rata-rata, fungsi ini menghasilkan breakdown per juri — juri A kasih rank berapa, juri B kasih rank berapa, dst.

// Langkah-langkahnya:
// 1. Loop tiap juri yang menilai submission ini
// Misal submission A dinilai oleh Budi, Siti, Andi — maka akan ada 3 iterasi.

// 2. Untuk setiap juri, lakukan hal yang sama seperti calculateAverageRank:

// Kumpulkan semua submission dalam kategori yang sama yang juga dinilai oleh juri itu
// Urutkan berdasarkan weighted_total_score dari tertinggi ke terendah
// Hitung peringkat submission ini di antara mereka (pakai standard competition ranking yang sama)
// 3. Simpan hasilnya ke dalam map dengan key = email juri

// Outputnya berbentuk seperti ini:

// {
//   "budi@email.com": { rank: 2, totalEvaluated: 5 },
//   "siti@email.com": { rank: 1, totalEvaluated: 5 },
//   "andi@email.com": { rank: 2, totalEvaluated: 5 }
// }
// rank → peringkat submission ini menurut juri tersebut (atau '-' kalau tidak ditemukan)
// totalEvaluated → berapa banyak submission yang dinilai oleh juri itu dalam kategori yang sama

// Perbedaan utama dengan calculateAverageRank:

//            calculateAverageRank	                                        calculateJudgeRankings
// Output	    Satu angka (rata-rata)	                                      Map per juri
// Kegunaan	  Sorting / urutan final	                                      Tampilan detail breakdown
// Skor yang  dipakai	calculated_weighted_score atau weighted_total_score	  weighted_total_score saja

/**
 * Returns a per-judge ranking map for a single submission.
 *
 * For each judge who evaluated the submission, computes that judge's rank
 * for this submission relative to all other submissions in the same category
 * that the same judge also evaluated.
 *
 * Returns `'-'` for rank when the submission is not found in a judge's
 * evaluations (shouldn't happen in normal usage).
 */
export function calculateJudgeRankings(
  submission: Submission,
  allJuryEvaluations: Submission['all_judgments'],
  allSubmissions: Submission[]
): Record<string, JudgeRankingEntry> {
  const result: Record<string, JudgeRankingEntry> = {};

  for (const evaluation of allJuryEvaluations) {
    const { judge_email } = evaluation;

    const sameCategoryScores = allSubmissions
      .filter(s => s.category === submission.category)
      .flatMap(s =>
        (s.all_judgments ?? [])
          .filter(j => j.judge_email === judge_email)
          .map(j => ({
            submissionId: s.id,
            score: j.weighted_total_score || 0,
            isTarget: s.id === submission.id,
          }))
      )
      .sort((a, b) => b.score - a.score);

    const rank = computeStandardRank(sameCategoryScores);

    result[judge_email] = {
      rank: rank > 0 ? rank : '-',
      totalEvaluated: sameCategoryScores.length,
    };
  }

  return result;
}

// ─────────────────────────────────────────────────────────────────────────────
// Sort comparators
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Sorts submissions by their average rank (ascending = best rank first).
 * When ranks are equal, falls back to total_score descending as a tiebreaker.
 */
function compareByAverageRank(
  a: Submission,
  b: Submission,
  allSubmissions: Submission[],
  direction: SortDirection
): number {
  const rankA = calculateAverageRank(a, allSubmissions);
  const rankB = calculateAverageRank(b, allSubmissions);

  // Push sentinel ranks (no data) to the end regardless of sort direction
  if (rankA === NO_RANK_SENTINEL && rankB === NO_RANK_SENTINEL) return 0;
  if (rankA === NO_RANK_SENTINEL) return 1;
  if (rankB === NO_RANK_SENTINEL) return -1;

  if (rankA !== rankB) {
    return direction === 'asc' ? rankA - rankB : rankB - rankA;
  }

  // Tiebreaker: higher total score wins (always descending)
  const scoreA =
    typeof a.total_score === 'number'
      ? a.total_score
      : parseFloat(String(a.total_score)) || 0;
  const scoreB =
    typeof b.total_score === 'number'
      ? b.total_score
      : parseFloat(String(b.total_score)) || 0;
  return scoreB - scoreA;
}

/**
 * Generic comparator for a single field, handling strings, numbers, and dates.
 */
function compareByField(
  a: Submission,
  b: Submission,
  field: string,
  direction: SortDirection
): number {
  let aValue: any = (a as any)[field] ?? '';
  let bValue: any = (b as any)[field] ?? '';

  if (field === 'total_score') {
    aValue = parseFloat(aValue) || 0;
    bValue = parseFloat(bValue) || 0;
  } else if (field === 'submitted_date' || field === 'judged_at') {
    aValue = new Date(aValue).getTime() || 0;
    bValue = new Date(bValue).getTime() || 0;
  } else if (typeof aValue === 'string' && typeof bValue === 'string') {
    aValue = aValue.toLowerCase();
    bValue = bValue.toLowerCase();
  }

  if (aValue < bValue) return direction === 'asc' ? -1 : 1;
  if (aValue > bValue) return direction === 'asc' ? 1 : -1;
  return 0;
}

// ─────────────────────────────────────────────────────────────────────────────
// Public sort entry-point
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Sorts a list of submissions by the given field and direction.
 *
 * For `avg_rank`, uses rank-based comparison with a score tiebreaker.
 * For all other fields, uses a generic comparator.
 *
 * @param submissions    — the list to sort (not mutated)
 * @param allSubmissions — full list needed for cross-submission rank calculation
 * @param field          — field to sort on
 * @param direction      — 'asc' or 'desc'
 */
export function sortSubmissions(
  submissions: Submission[],
  allSubmissions: Submission[],
  field: string,
  direction: SortDirection
): Submission[] {
  return [...submissions].sort((a, b) =>
    field === 'avg_rank'
      ? compareByAverageRank(a, b, allSubmissions, direction)
      : compareByField(a, b, field, direction)
  );
}
